import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, TrackByFunction, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { IColumn } from '@app/shared/interfaces/column.interface';
import { TabsModule } from 'primeng/tabs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import * as XLSX from 'xlsx';
import { Ripple } from 'primeng/ripple';
import { PageHeaderComponent } from '@shared/components/page-header/page-header.component';
import { CardComponent } from '@shared/components/card/card.component';
import { forkJoin, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import {
    IAccountingMonthlyReport,
    IAccountingSummaryUnit,
    TotalSummary,
    IReservationAccountingResponse,
    TripCountData,
    FilterOptions,
    ReservationColumn,
    TabItem,
    TableRow,
    ReservationRowData,
    FlightBasedDetails,
    ReservationProducts,
    AccountingReportFilter,
    TableState,
    Ports,
    Providers,
    Airlines
} from '@shared/interfaces/accounting-report.interface';
import { AccountingReportService } from '@shared/services/accounting-report-service.service';

type AccountingReportQuery = Pick<AccountingReportFilter, 'page' | 'size' | 'sortBy' | 'sortDirection'> &
    Partial<Omit<AccountingReportFilter, 'page' | 'size' | 'sortBy' | 'sortDirection'>>;

@Component({
    selector: 'app-accounting-report',
    standalone: true,
    imports: [
        TableModule,
        Ripple,
        CommonModule,
        TabsModule,
        ButtonModule,
        DatePickerModule,
        FormsModule,
        SelectModule,
        DialogModule,
        PageHeaderComponent,
        CardComponent
    ],
    templateUrl: './accounting-report.component.html',
    styleUrls: ['./accounting-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountingReportComponent implements OnInit {
    filterDialogVisible = false;
    data = [{ oneWay: 0, roundTrip: 0 }];
    errorMsg = '';
    activeQuery: AccountingReportQuery | null = null;
    first = 0;
    portLoading = false;
    loading = false;
    summaryData: IAccountingSummaryUnit[] = [];
    expandedRows: Record<string, boolean> = {};
    reservations: ReservationRowData[] = [];
    entriesPerPage = 10;
    totalRecords = 0;
    tableState: TableState = {
        page: 0,
        size: 10,
        sortBy: null,
        sortDirection: null
    };
    ports: Ports[] = [];
    providers: Providers[] = [];
    airlines: Airlines[] = [];
    private dropdownDataCache: { ports?: Ports[]; providers?: Providers[]; airlines?: Airlines[] } = {};
    private dropdownDataLoaded = false;
    reservationCols: ReservationColumn[] = [
        { key: 'reservationType', label: 'Rez Type' },
        { key: 'flightType', label: 'Flight Type' },
        { key: 'pnr', label: 'PNR' },
        { key: 'airlinePnr', label: 'Airline PNR' },
        { key: 'agent', label: 'Agent' },
        { key: 'airline', label: 'Airline' },
        { key: 'resDate', label: 'Res Date' },

        { key: 'flightDate', label: 'Flight Date' },
        { key: 'fromPort', label: 'From Port' },
        { key: 'toPort', label: 'To Port' },
        { key: 'fromCountry', label: 'From Country' },
        { key: 'toCountry', label: 'To Country' },
        { key: 'tax', label: 'Tax' },
        { key: 'total', label: 'Total' }
    ];
    getCurrentYear = new Date().getFullYear();
    tabs: TabItem[] = [
        { title: this.getCurrentYear - 1, value: 0 },
        { title: this.getCurrentYear, value: 1 },
        { title: this.getCurrentYear + 1, value: 2 }
    ];

    onRowExpand(e: { data: ReservationRowData }): void {
        const key = e.data?.__key;
        this.expandedRows = key ? { [key]: true } : {};
    }

    onTabChange(index: any): void {
        this.selectedTabIndex = index;
        this.loadByMonthlyData(this.tabs[index].title);
    }
    cols: IColumn[] = [
        { key: '', label: '' },
        { key: 'jan', label: 'Jan' },
        { key: 'feb', label: 'Feb' },
        { key: 'mar', label: 'Mar' },
        { key: 'apr', label: 'Apr' },
        { key: 'may', label: 'May' },
        { key: 'jun', label: 'Jun' },
        { key: 'jul', label: 'Jul' },
        { key: 'aug', label: 'Aug' },
        { key: 'sep', label: 'Sep' },
        { key: 'oct', label: 'Oct' },
        { key: 'nov', label: 'Nov' },
        { key: 'dec', label: 'Dec' },
        { key: 'total', label: 'Total' }
    ];

    selectedTabIndex = 0;
    totalByMonth: IAccountingMonthlyReport = {
        monthly: [],
        total: {
            revenue: { amount: 0, currency: '' },
            cost: { amount: 0, currency: '' },
            profit: { amount: 0, currency: '' },
            profitRate: 0,
            marginPercentage: 0
        },
        year: this.getCurrentYear,
        totalPassengerCount: 0,
        totalReservationCount: 0
    };

    rows: TableRow[] = [
        { key: 'revenue', label: 'Revenue' },
        { key: 'cost', label: 'Cost' },
        { key: 'profit', label: 'Result' },
        { key: 'margin', label: 'Margin' }
    ];

    reservationTypes: { name: string; value: string }[] = [
        { name: 'Roundtrip', value: 'RT' },
        { name: 'Oneway', value: 'OW' }
    ];

    filters: FilterOptions = {
        reservationFrom: null,
        reservationTo: null,
        flightDateFrom: null,
        flightDateTo: null,
        flightFromPort: null,
        flightToPort: null,
        reservationType: null,
        provider: null,
        airline: null
    };

    constructor(
        private accountingReportService: AccountingReportService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        // Varsayılan olarak son bir ayı ayarla
        const today = new Date();
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(today.getMonth() - 1);

        this.filters.reservationFrom = oneMonthAgo;
        this.filters.reservationTo = today;

        this.loadByMonthlyData(this.tabs[this.selectedTabIndex].title);
        this.getDropdownOptions();

        // Varsayılan filtreleri uygula
        this.applyFilters();
    }

    loadAccountingData(filterParams: any): void {
        this.tableState.page = 0;
        this.getTripCount(filterParams);
        this.getSummaryData(filterParams);
        this.getReservationDetails(filterParams);
    }
    getDropdownOptions(): void {
        if (this.dropdownDataLoaded && this.dropdownDataCache.ports) {
            this.ports = this.dropdownDataCache.ports;
            this.providers = this.dropdownDataCache.providers || [];
            this.airlines = this.dropdownDataCache.airlines || [];
            return;
        }

        this.portLoading = true;

        forkJoin({
            ports: this.accountingReportService.getPorts().pipe(
                catchError((err) => {
                    console.error('Failed to load ports:', err);
                    return of([]);
                })
            ),
            providers: this.accountingReportService.getProviders().pipe(
                catchError((err) => {
                    console.error('Failed to load providers:', err);
                    return of([]);
                })
            ),
            airlines: this.accountingReportService.getAirlines().pipe(
                catchError((err) => {
                    console.error('Failed to load airlines:', err);
                    return of([]);
                })
            )
        }).subscribe({
            next: (data) => {
                this.ports = data.ports;
                this.providers = data.providers;
                this.airlines = data.airlines;

                this.dropdownDataCache = {
                    ports: data.ports,
                    providers: data.providers,
                    airlines: data.airlines
                };
                this.dropdownDataLoaded = true;
                this.portLoading = false;
                this.cdr.markForCheck();
            },
            error: (err) => {
                this.portLoading = false;
                this.errorMsg = 'Failed to load dropdown data';
                console.error('Failed to load dropdown data:', err);
                this.cdr.markForCheck();
            }
        });
    }

    getTripCount(filterParams: AccountingReportFilter): void {
        this.accountingReportService.getTripCount(filterParams).subscribe({
            next: (data: TripCountData) => {
                const oneWay = data?.oneWayCount ?? 0;
                const roundTrip = data?.roundTripCount ?? 0;
                this.data = [{ oneWay, roundTrip }];
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Failed to load trip counts', err);
                this.cdr.markForCheck();
            }
        });
    }

    getSummaryData(filterParams: any): void {
        this.loading = true;
        this.accountingReportService.getAccountingSummary(filterParams).subscribe({
            next: (data: IAccountingSummaryUnit[]) => {
                this.summaryData = data;
                this.loading = false;
                this.cdr.markForCheck();
            },
            error: (error) => {
                this.loading = false;
                console.error('Failed to load summary data', error);
                this.cdr.markForCheck();
            }
        });
    }

    loadByMonthlyData(year: number): void {
        this.accountingReportService.getAccountingMonthlyReport(year, null).subscribe({
            next: (res: IAccountingMonthlyReport) => {
                this.totalByMonth.monthly = res.monthly;
                this.totalByMonth.total = res.total;
                this.cdr.markForCheck();
            },
            error: (err) => {
                console.error('Failed to load monthly data', err);
                this.cdr.markForCheck();
            }
        });
    }

    onLazyLoad(event: TableLazyLoadEvent): void {
        if (!this.activeQuery) {
            // filtre uygulanmadan paginator tetiklendiyse hiçbir şey yapma
            return;
        }

        const rows = event.rows ?? this.tableState.size;
        const first = event.first ?? this.first;

        this.tableState.size = rows;
        this.tableState.page = Math.floor(first / rows);
        this.first = first;

        if (event.sortField) {
            this.tableState.sortBy = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
            this.tableState.sortDirection = event.sortOrder === 1 ? 'ASC' : 'DESC';
        }

        const req: AccountingReportQuery = {
            ...this.activeQuery, // TÜM FİLTRELER BURADA
            page: this.tableState.page,
            size: this.tableState.size,
            sortBy: this.tableState.sortBy,
            sortDirection: this.tableState.sortDirection
        };

        this.getReservationDetails(req as AccountingReportFilter);
    }

    clearFilters(): void {
        this.filters = {
            reservationFrom: null,
            reservationTo: null,
            flightDateFrom: null,
            flightDateTo: null,
            flightFromPort: null,
            flightToPort: null,
            reservationType: null,
            provider: null,
            airline: null
        };
    }

    openFilterDialog(): void {
        this.filterDialogVisible = true;
        // Preload dropdown data if not already loaded
        if (!this.dropdownDataLoaded) {
            this.getDropdownOptions();
        }
    }

    // TrackBy functions for better performance
    trackByPortCode: TrackByFunction<Ports> = (index: number, port: Ports) => port.code;
    trackByProviderCode: TrackByFunction<Providers> = (index: number, provider: Providers) => provider.code;
    trackByAirlineCode: TrackByFunction<Airlines> = (index: number, airline: Airlines) => airline.code;
    trackByReservationKey: TrackByFunction<ReservationRowData> = (index: number, reservation: ReservationRowData) =>
        reservation.__key || index;

    applyFilters(): void {
        this.errorMsg = '';
        const rFrom = this.filters.reservationFrom;
        const rTo = this.filters.reservationTo;
        const fFrom = this.filters.flightDateFrom;
        const fTo = this.filters.flightDateTo;

        const isRangeFilled = (from?: Date | null, to?: Date | null) => !!from && !!to;
        const hasReservationRange = isRangeFilled(rFrom, rTo);
        const hasFlightRange = isRangeFilled(fFrom, fTo);
        const hasPartial = (from?: Date | null, to?: Date | null) => (!!from || !!to) && !isRangeFilled(from, to);

        if (hasPartial(rFrom, rTo) || hasPartial(fFrom, fTo)) {
            this.errorMsg = 'Both "From" and "To" dates must be filled together.';
            return;
        }

        if (!hasReservationRange && !hasFlightRange) {
            this.errorMsg = 'Please select at least one complete date range (Reservation or Flight).';
            return;
        }

        const toBeforeFrom = (from?: Date | null, to?: Date | null) => isRangeFilled(from, to) && new Date(to!) < new Date(from!);

        if (toBeforeFrom(rFrom, rTo)) {
            this.errorMsg = 'Reservation "To" date cannot be earlier than "From" date.';
            return;
        }

        if (toBeforeFrom(fFrom, fTo)) {
            this.errorMsg = 'Flight "To" date cannot be earlier than "From" date.';
            return;
        }

        const q: AccountingReportQuery = {
            page: 0,
            size: this.tableState.size,
            sortBy: this.tableState.sortBy,
            sortDirection: this.tableState.sortDirection,
            ...(this.filters.reservationFrom &&
                this.filters.reservationTo && {
                    reservationDateFrom: this.formatDate(this.filters.reservationFrom),
                    reservationDateTo: this.formatDate(this.filters.reservationTo)
                }),
            ...(this.filters.flightDateFrom &&
                this.filters.flightDateTo && {
                    flightDateFrom: this.formatDate(this.filters.flightDateFrom),
                    flightDateTo: this.formatDate(this.filters.flightDateTo)
                }),
            ...(this.filters.flightFromPort && { depPort: this.filters.flightFromPort }), // <<< KOD
            ...(this.filters.flightToPort && { arrPort: this.filters.flightToPort }),
            ...(this.filters.reservationType?.value && { reservationType: this.filters.reservationType.value }),
            ...(this.filters.provider && { providerCode: this.filters.provider }), // provider zaten string ise ok
            ...(this.filters.airline && { airlineCode: this.filters.airline })
        };

        this.activeQuery = q;
        this.first = 0;
        this.tableState.page = 0;

        this.loadAccountingData(q);
        this.filterDialogVisible = false;
    }

    private formatDate(date: Date): string {
        if (!date) return '';
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }
    getReservationDetails(filter: AccountingReportFilter): void {
        this.loading = true;
        this.accountingReportService.getReservationDetails(filter).subscribe({
            next: (res: IReservationAccountingResponse) => {
                this.reservations = (res.content ?? []).map((r, i) => ({
                    ...r,
                    __key: `${r.airline}-${r.reservationDate}-${r?.from?.code}-${r?.to?.code}-${i}`
                }));
                this.totalRecords = res.totalElements ?? 0;
                this.loading = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.loading = false;
                console.error('Failed to load reservation details');
                this.cdr.markForCheck();
            }
        });
    }

    getDepartureBreakdown(reservation: ReservationRowData): FlightBasedDetails | undefined {
        return reservation?.flightBasedDetails?.find((detail: FlightBasedDetails) => detail.segmentType === 'DEPARTURE');
    }

    getReturnBreakdown(reservation: ReservationRowData): FlightBasedDetails | undefined {
        return reservation?.flightBasedDetails?.find((detail: FlightBasedDetails) => detail.segmentType === 'RETURN');
    }
    getTotalSummary(reservation: ReservationRowData): TotalSummary {
        const totalSummary: TotalSummary = {
            total: reservation?.total || {
                revenue: { amount: 0, currency: '' },
                cost: { amount: 0, currency: '' },
                profit: { amount: 0, currency: '' },
                profitRate: 0,
                marginPercentage: 0
            },
            commission: reservation?.commission || { amount: 0, currency: '' },
            tax: reservation?.totalTax || { amount: 0, currency: '' },
            ticket: reservation?.ticket || {
                revenue: { amount: 0, currency: '' },
                cost: { amount: 0, currency: '' },
                profit: { amount: 0, currency: '' },
                profitRate: 0,
                marginPercentage: 0
            },
            reservationProducts: reservation?.reservationProducts || {},
            other: reservation?.other || {
                revenue: { amount: 0, currency: '' },
                cost: { amount: 0, currency: '' },
                profit: { amount: 0, currency: '' },
                profitRate: 0,
                marginPercentage: 0
            },
            paymentMethod: reservation?.paymentMethod || ''
        };
        return totalSummary;
    }
    getShoppingCategoryKeys(reservation: ReservationRowData, segmentType: 'DEPARTURE' | 'RETURN'): string[] {
        const breakdown = reservation?.flightBasedDetails?.find((detail: FlightBasedDetails) => detail.segmentType === segmentType);
        return breakdown?.shoppingCategories ? Object.keys(breakdown.shoppingCategories) : [];
    }

    getFlightValue(reservation: ReservationRowData, segmentType: 'DEPARTURE' | 'RETURN', valueType: 'revenue' | 'cost'): string {
        const breakdown = reservation?.flightBasedDetails?.find((detail: FlightBasedDetails) => detail.segmentType === segmentType);
        if (!breakdown?.flight) return '-';

        if (valueType === 'revenue') {
            return `${breakdown.flight.revenue?.amount || 0} ${breakdown.flight.revenue?.currency || ''}`;
        } else if (valueType === 'cost') {
            return `${breakdown.flight.cost?.amount || 0} ${breakdown.flight.cost?.currency || ''}`;
        }
        return '-';
    }
    getShoppingCategoryValue(
        reservation: ReservationRowData,
        segmentType: 'DEPARTURE' | 'RETURN',
        category: string,
        valueType: 'revenue' | 'cost'
    ): string {
        const breakdown = reservation?.flightBasedDetails?.find((detail: FlightBasedDetails) => detail.segmentType === segmentType);
        const categoryData = breakdown?.shoppingCategories?.[category];

        if (!categoryData) return '-';

        if (valueType === 'revenue') {
            return `${categoryData.revenue?.amount || 0} ${categoryData.revenue?.currency || ''}`;
        } else if (valueType === 'cost') {
            return `${categoryData.cost?.amount || 0} ${categoryData.cost?.currency || ''}`;
        }
        return '-';
    }
    humanizeKey(key: string): string {
        if (!key) return '';
        return key
            .replace(/_/g, ' ')
            .split(' ')
            .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w))
            .join(' ');
    }

    getTotalSummaryValue(
        reservation: ReservationRowData,
        column: 'ticket' | 'other' | 'total' | 'commission',
        rowType: 'revenue' | 'cost' | 'profit' | 'payment type'
    ): string {
        const totalSummary = this.getTotalSummary(reservation);

        if (rowType === 'payment type') {
            if (column === 'ticket') {
                return totalSummary.paymentMethod || '';
            }
            return '';
        }

        let data: any = null;
        switch (column) {
            case 'ticket':
                data = totalSummary.ticket;
                break;
            case 'other':
                data = totalSummary.other;
                break;
            case 'total':
                data = totalSummary.total;
                break;
            case 'commission':
                data = { revenue: totalSummary.commission, cost: { amount: 0, currency: '' }, profit: totalSummary.commission };
                break;
        }

        if (!data) return '';

        switch (rowType) {
            case 'revenue':
                return `${data.revenue?.amount || 0} ${data.revenue?.currency || ''}`;
            case 'cost':
                return `${data.cost?.amount || 0} ${data.cost?.currency || ''}`;
            case 'profit':
                return `${data.profit?.amount || 0} ${data.profit?.currency || ''}`;
            default:
                return '';
        }
    }

    getReservationProductKeys(reservation: ReservationRowData): string[] {
        return reservation?.reservationProducts ? Object.keys(reservation.reservationProducts) : [];
    }

    getReservationProductValue(reservation: ReservationRowData, product: string, valueType: 'revenue' | 'cost' | 'profit'): string {
        const productData = reservation?.reservationProducts?.[product];
        if (!productData) return '-';

        if (valueType === 'revenue') {
            return `${productData.revenue?.amount || 0} ${productData.revenue?.currency || ''}`;
        } else if (valueType === 'cost') {
            return `${productData.cost?.amount || 0} ${productData.cost?.currency || ''}`;
        } else if (valueType === 'profit') {
            return `${productData.profit?.amount || 0} ${productData.profit?.currency || ''}`;
        }
        return '';
    }

    exportReport(): void {
        const resHeader = [
            'Rez Type',
            'Flight Type',
            'Agent',
            'Airline',
            'Res Date',
            'Flight Date',
            'From Port',
            'To Port',
            'From Country',
            'To Country',
            'Tax',
            'Total'
        ];

        const resRows = this.reservations.map((r) => ({
            'Rez Type': r.reservationType ?? '',
            'Flight Type': r.flightType ?? '',
            Agent: r.agent ?? '',
            Airline: r.airline ?? '',
            'Res Date': r.reservationDate ?? '',
            'Flight Date': r.flightDate ?? '',
            'From Port': r?.from?.code ?? '',
            'To Port': r?.to?.code ?? '',
            'From Country': r?.from?.country ?? '',
            'To Country': r?.to?.country ?? '',
            Tax: r?.totalTax ? `${r.totalTax.amount} ${r.totalTax.currency}` : '',
            Total: r?.total?.revenue ? `${r.total.revenue.amount} ${r.total.revenue.currency}` : ''
        }));

        const wsReservations = XLSX.utils.json_to_sheet(resRows, { header: resHeader });

        const summaryRows = this.summaryData.map((s) => ({
            Category: s.categoryName,
            'Revenue Amount': s.metrics.revenue.amount,
            'Revenue Currency': s.metrics.revenue.currency,
            'Cost Amount': s.metrics.cost.amount,
            'Cost Currency': s.metrics.cost.currency,
            'Profit Amount': s.metrics.profit.amount,
            'Profit Currency': s.metrics.profit.currency,
            'Margin %': s.metrics.marginPercentage
        }));
        const wsSummary = XLSX.utils.json_to_sheet(summaryRows);

        const header = ['Metric', ...this.totalByMonth.monthly.map((m) => this.cols[m.month].label), 'Total'];
        const rowOf = (key: 'revenue' | 'cost' | 'profit' | 'margin') => [
            key.toUpperCase(),
            ...this.totalByMonth.monthly.map((m) => {
                if (key === 'margin') {
                    return m.metrics.marginPercentage;
                }
                const v = m.metrics[key as 'revenue' | 'cost' | 'profit'];
                return `${v.amount} ${v.currency}`;
            }),
            key === 'margin'
                ? this.totalByMonth.total.marginPercentage
                : `${(this.totalByMonth.total as any)[key].amount} ${(this.totalByMonth.total as any)[key].currency}`
        ];
        const wsMonthly = XLSX.utils.aoa_to_sheet([header, rowOf('revenue'), rowOf('cost'), rowOf('profit'), rowOf('margin')]);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
        XLSX.utils.book_append_sheet(wb, wsMonthly, 'TotalByMonth');
        XLSX.utils.book_append_sheet(wb, wsReservations, 'Reservations');

        XLSX.writeFile(wb, `AccountingReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
    }
}
